import React from "react";
import { cn } from "@/lib/utils";
import { useFocusTrap } from "@/design-system/hooks/useFocusTrap";

/**
 * Modal — dialog primitive.
 *
 * A11y (Phase 6 hardened):
 *   • `role="dialog"` + `aria-modal="true"`.
 *   • On open: saves `document.activeElement` (the trigger), moves focus
 *     to the first focusable descendant of the panel (or the panel).
 *   • On close: restores focus to the saved trigger — even if `onOpenChange`
 *     fires from outside the primitive (backdrop / ESC / footer button).
 *   • Tab / Shift+Tab are trapped inside the panel (wrap end-to-start).
 *   • ESC closes.
 *   • Clicking the backdrop closes UNLESS `dismissOnBackdrop=false`.
 *
 * Rendered inline (no portal) — consumers can wrap in their own portal
 * if they need one. For Phase 1 the primitive is intentionally
 * dependency-free (no `@radix-ui/react-dialog`) so the design-system
 * package stays self-contained.
 *
 * Guardrails:
 *   • Backdrop = flat translucent black (no blurred glass).
 *   • Panel = flat surface + drop shadow (no glow).
 *   • Radius = 14px (`--ds-radius-xl`) — the max in the token scale.
 */

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  footer,
  dismissOnBackdrop = true,
  returnFocusRef,
  className,
  "data-testid": testId,
  children,
}) {
  const panelRef = React.useRef(null);
  const labelId = React.useId();
  const descId = React.useId();

  useFocusTrap(panelRef, open, {
    onEscape: () => onOpenChange?.(false),
    returnFocusRef,
  });

  if (!open) return null;

  return (
    <div
      className={cn(
        "fixed inset-0 flex items-center justify-center",
        "bg-black/50 p-4"
      )}
      style={{ zIndex: "var(--ds-z-modal)" }}
      data-testid={testId ? `${testId}-backdrop` : undefined}
      onMouseDown={(e) => {
        if (dismissOnBackdrop && e.target === e.currentTarget) {
          onOpenChange?.(false);
        }
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? labelId : undefined}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        data-testid={testId}
        className={cn(
          "w-full max-w-lg rounded-[var(--ds-radius-xl)] border",
          "bg-[var(--ds-color-surface)] border-[var(--ds-color-border)]",
          "shadow-[var(--ds-shadow-modal)]",
          "outline-none",
          className
        )}
      >
        {(title || description) && (
          <div className="px-5 pt-5 pb-3">
            {title && (
              <h2 id={labelId} className="text-[18px] font-semibold text-[var(--ds-color-ink)] leading-snug">
                {title}
              </h2>
            )}
            {description && (
              <p id={descId} className="mt-1 text-[13px] text-[var(--ds-color-ink-muted)] leading-snug">
                {description}
              </p>
            )}
          </div>
        )}
        <div className="px-5 pb-5 text-[14px] text-[var(--ds-color-ink)]">
          {children}
        </div>
        {footer && (
          <div className="flex items-center justify-end gap-3 border-t border-[var(--ds-color-border)] px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export default Modal;
